import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { 
  SurgeonSubsetTemplate, 
  SurgeonSubsetTemplateInsert,
  PayerScopeType,
} from '@/lib/authorization/subsetTypes';
import { toast } from 'sonner';

/**
 * Hook to fetch all surgeon subset templates (for admin view)
 */
export function useAllSurgeonSubsets() {
  return useQuery({
    queryKey: ['surgeon-subsets', 'all'],
    queryFn: async (): Promise<SurgeonSubsetTemplate[]> => {
      const { data, error } = await supabase
        .from('surgeon_subset_templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as SurgeonSubsetTemplate[];
    },
  });
}

/**
 * Hook to fetch active subsets for a specific surgeon
 */
export function useSurgeonActiveSubsets(surgeonId: string | null) {
  return useQuery({
    queryKey: ['surgeon-subsets', surgeonId, 'active'],
    queryFn: async (): Promise<SurgeonSubsetTemplate[]> => {
      if (!surgeonId) return [];
      
      const { data, error } = await supabase
        .from('surgeon_subset_templates')
        .select('*')
        .eq('surgeon_id', surgeonId)
        .eq('is_active', true)
        .is('superseded_by', null)
        .order('primary_cpt');

      if (error) throw error;
      return (data || []) as SurgeonSubsetTemplate[];
    },
    enabled: !!surgeonId,
  });
}

/**
 * Hook to fetch a specific subset for a surgeon + primary CPT combo
 * Used during the New Request flow
 */
export function useSurgeonSubsetForProcedure(
  surgeonId: string | null, 
  primaryCpt: string | null,
  payerScope?: PayerScopeType,
  specificPayerId?: string | null
) {
  return useQuery({
    queryKey: ['surgeon-subsets', surgeonId, primaryCpt, payerScope, specificPayerId],
    queryFn: async (): Promise<SurgeonSubsetTemplate | null> => {
      if (!surgeonId || !primaryCpt) return null;

      // Build query for active, non-superseded templates
      let query = supabase
        .from('surgeon_subset_templates')
        .select('*')
        .eq('surgeon_id', surgeonId)
        .eq('primary_cpt', primaryCpt)
        .eq('is_active', true)
        .is('superseded_by', null);

      // Priority order: specific payer > payer type > all
      if (specificPayerId) {
        // First try specific payer
        const { data: specificData } = await query
          .eq('payer_scope', 'specific')
          .eq('specific_payer_id', specificPayerId)
          .maybeSingle();
        
        if (specificData) return specificData as SurgeonSubsetTemplate;
      }

      // Then try payer type scope
      if (payerScope && payerScope !== 'all' && payerScope !== 'specific') {
        const { data: scopeData } = await supabase
          .from('surgeon_subset_templates')
          .select('*')
          .eq('surgeon_id', surgeonId)
          .eq('primary_cpt', primaryCpt)
          .eq('is_active', true)
          .is('superseded_by', null)
          .eq('payer_scope', payerScope)
          .maybeSingle();
        
        if (scopeData) return scopeData as SurgeonSubsetTemplate;
      }

      // Finally try 'all' scope
      const { data: allData, error } = await supabase
        .from('surgeon_subset_templates')
        .select('*')
        .eq('surgeon_id', surgeonId)
        .eq('primary_cpt', primaryCpt)
        .eq('is_active', true)
        .is('superseded_by', null)
        .eq('payer_scope', 'all')
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      return allData as SurgeonSubsetTemplate | null;
    },
    enabled: !!surgeonId && !!primaryCpt,
  });
}

/**
 * Hook to create a new subset template
 */
export function useCreateSurgeonSubset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SurgeonSubsetTemplateInsert) => {
      const { data: user } = await supabase.auth.getUser();
      if (!user?.user?.id) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('surgeon_subset_templates')
        .insert({
          ...input,
          created_by: user.user.id,
          version: 1,
        })
        .select()
        .single();

      if (error) throw error;
      return data as SurgeonSubsetTemplate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['surgeon-subsets'] });
      toast.success('Surgeon subset template created');
    },
    onError: (error: Error) => {
      console.error('Failed to create subset:', error);
      toast.error('Failed to create subset template');
    },
  });
}

/**
 * Hook to update (version) an existing subset template
 * Creates a new version and marks the old one as superseded
 */
export function useUpdateSurgeonSubset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      id, 
      updates 
    }: { 
      id: string; 
      updates: Partial<SurgeonSubsetTemplateInsert>;
    }) => {
      const { data: user } = await supabase.auth.getUser();
      if (!user?.user?.id) throw new Error('Not authenticated');

      // Fetch the existing template
      const { data: existing, error: fetchError } = await supabase
        .from('surgeon_subset_templates')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;
      const existingTemplate = existing as SurgeonSubsetTemplate;

      // Create new version
      const { data: newVersion, error: insertError } = await supabase
        .from('surgeon_subset_templates')
        .insert({
          surgeon_id: existingTemplate.surgeon_id,
          primary_cpt: existingTemplate.primary_cpt,
          subset_cpts: updates.subset_cpts ?? existingTemplate.subset_cpts,
          payer_scope: updates.payer_scope ?? existingTemplate.payer_scope,
          specific_payer_id: updates.specific_payer_id ?? existingTemplate.specific_payer_id,
          is_active: updates.is_active ?? existingTemplate.is_active,
          notes: updates.notes ?? existingTemplate.notes,
          version: existingTemplate.version + 1,
          created_by: user.user.id,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Mark old version as superseded
      const { error: updateError } = await supabase
        .from('surgeon_subset_templates')
        .update({ 
          superseded_by: (newVersion as SurgeonSubsetTemplate).id,
          is_active: false,
        })
        .eq('id', id);

      if (updateError) throw updateError;

      return newVersion as SurgeonSubsetTemplate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['surgeon-subsets'] });
      toast.success('Surgeon subset template updated (new version created)');
    },
    onError: (error: Error) => {
      console.error('Failed to update subset:', error);
      toast.error('Failed to update subset template');
    },
  });
}

/**
 * Hook to deactivate a subset template
 */
export function useDeactivateSurgeonSubset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('surgeon_subset_templates')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['surgeon-subsets'] });
      toast.success('Subset template deactivated');
    },
    onError: (error: Error) => {
      console.error('Failed to deactivate subset:', error);
      toast.error('Failed to deactivate subset template');
    },
  });
}

/**
 * Hook to reactivate a subset template
 */
export function useReactivateSurgeonSubset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('surgeon_subset_templates')
        .update({ is_active: true })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['surgeon-subsets'] });
      toast.success('Subset template reactivated');
    },
    onError: (error: Error) => {
      console.error('Failed to reactivate subset:', error);
      toast.error('Failed to reactivate subset template');
    },
  });
}
